import { getFaqByIdAPI } from '@/src/apis/rest/faq';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { BUTTON_CONST } from '@/src/constants/buttons';
import PATH from '@/src/routes/path';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DetailSctionRenderer } from '@/src/components/organism/DetailSection/renderer';
import DetailSection from '@/src/components/organism/DetailSection';
import { NAV_CONST } from '@/src/constants/nav';
import { OPTIONS_CONST } from '@/src/constants/options';

const BACK_PATH = PATH.FAQ_LIST;
const EDIT_PATH = PATH.FAQ_EDIT;

export default function FaqDetailPage() {
  // util
  const navigate = useNavigate();
  const { id } = useParams();

  // query
  const { data: faqData } = useQuery({
    queryKey: ['faq', id],
    queryFn: () => getFaqByIdAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  // evnet
  const handleGoBack = () => {
    navigate(-1);
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>

          <Link to={EDIT_PATH.replace(':id', String(id))}>
            <Button className="w-40" buttonSize="medium">
              <div className="flex items-center gap-x-2">
                {BUTTON_CONST.editShort}
              </div>
            </Button>
          </Link>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[NAV_CONST.faq, NAV_CONST.faqDetail]}
        pageTitle={NAV_CONST.faqDetail}
      />
      <div className="mb-[100px] flex flex-col gap-6">
        <DetailSection
          sectionTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                FAQ 정보
              </span>
            </div>
          }
          data={faqData}
          contents={[
            [
              {
                key: 'faqCategoryType',
                lable: '카테고리',
              },
              {
                key: 'showedYn',
                lable: '노출 여부',
                valueSetter: data => (data?.showedYn ? 'true' : 'false'),
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: OPTIONS_CONST.showed,
                      key: 'true',
                      badgeColor: 'lightGreen',
                    },
                    {
                      label: OPTIONS_CONST.notShowed,
                      key: 'false',
                      badgeColor: 'lightGray',
                    },
                  ],
                }),
              },
            ],
            [
              {
                key: 'createdAt',
                lable: '등록 일시',
                render: DetailSctionRenderer.DATE(),
              },
              {
                key: ['administrator', 'name'],
                lable: '등록 관리자',
              },
            ],
          ]}
          bottomContents={
            <article className="border-gray10 rounded-lg border p-2">
              <header className="border-b border-gray2 p-4">
                <h3 className="flex items-start gap-1 text-b18sb text-gray9">
                  {faqData?.title}
                </h3>
              </header>
              <div className="min-h-[500px] px-4 py-6">
                <div
                  style={{
                    all: 'unset',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                  dangerouslySetInnerHTML={{ __html: faqData?.contents || '' }}
                />
              </div>
            </article>
          }
        />
      </div>
      {renderBottomFixedContent()}
    </>
  );
}
